import React, { ReactNode, useCallback, CSSProperties } from 'react';
import {
  ExtendedRefs,
  FloatingContext,
  FloatingFocusManager,
  FloatingPortal,
  useTransitionStyles,
} from '@floating-ui/react';
import { useFocusRing } from '@react-aria/focus';
import classNames from 'classnames';
import { getKeyFromItem } from '../../utils/shared';
import { CSSValueWithLength, getZIndex } from '../../styles';
import { FLOATING_ROOT_ID } from '../../constants';
import { AriaFocusProps } from '../../types/common';
import { dropdown_item_group_css, dropdown_item_group_focus_style } from '../dropdown/styles';
import { semantic_colors, spacing } from '../../foundation';
import styled from '@emotion/styled';
import {
  DEFAULT_DROPDOWN_COMPOSITE_MAX_HEIGHT,
  DROPDOWN_COMPOSITE_LIST_MIN_WIDTH,
  XSMALL_DROPDOWN_COMPOSITE_MAX_HEIGHT,
} from './constants';
import { getDropdownCompositeOptionWidth } from './styles';
import { DropdownCompositeInternalOption } from './DropdownCompositeInternalOption';
import { DropdownCompositeOption, DropdownCompositeSize } from './types';
import { FormField } from '../form';
import { Stack } from '../stack';
import { TextButton } from '../button';
import { Button } from '../button';
import { IconReset } from '../icons/generated';
import { getInputSpacingBySize } from '../input/styles';

export interface DropdownCompositeOptionsProps<OptionValue> {
  className?: string;
  /**
   * 기본값으로 [ZIndexOrder](https://github.com/croquiscom/pds/blob/main/src/styles/zIndex.ts) 규칙을 적용합니다.
   * @default 1030
   */
  zIndex?: number;
  optionWidth?: CSSValueWithLength;
  noDataText?: ReactNode;
  initialFocus?: number;
  options: Array<DropdownCompositeOption<OptionValue>>;
  value?: OptionValue;
  onClose: () => void;
  keyExtractor?: (item: DropdownCompositeOption<OptionValue>, index: number) => string | number;
  onChange?: (value: OptionValue) => void;
  focusIndex?: number | null;
  getFloatingProps: (props: Record<string, unknown>) => Record<string, unknown>;
  getItemProps: (props: Record<string, unknown>) => Record<string, unknown>;
  context: FloatingContext;
  floatingStyles: CSSProperties;
  refs: ExtendedRefs<Element>;
  setRefs: (node: HTMLElement | null, index: number) => void;
  setFocusIndex?: (index: number) => void;
  onSubmit?: () => void;
  onReset?: () => void;
  /** @default 'medium' */
  size?: DropdownCompositeSize;
}

export const DropdownCompositeOptions = <OptionValue,>({
  className,
  zIndex = getZIndex('dropdown'),
  initialFocus,
  options,
  optionWidth,
  onClose,
  noDataText,
  value: valueProp,
  unstableFocusVisible,
  keyExtractor = getKeyFromItem,
  onChange,
  focusIndex,
  context,
  getFloatingProps,
  getItemProps,
  floatingStyles,
  refs,
  setRefs,
  setFocusIndex,
  onSubmit,
  onReset,
  size = 'medium',
}: DropdownCompositeOptionsProps<OptionValue> & AriaFocusProps) => {
  const { isFocusVisible, focusProps } = useFocusRing({ within: true, isTextInput: true });
  const selectedIndex = options.findIndex(({ value }) => value === valueProp);
  const selectedOption = options[selectedIndex];
  const { isMounted, styles } = useTransitionStyles(context, {
    duration: 100,
    initial: {
      opacity: 0,
    },
    common: {
      transitionTimingFunction: 'linear',
    },
  });

  const handleChange = useCallback(
    (value: OptionValue) => {
      onChange?.(value);
    },
    [onChange],
  );

  const internalOptionProps = {
    setRefs,
    selectedIndex,
    focusIndex,
    onChange: handleChange,
    keyExtractor,
    getItemProps,
    width: optionWidth,
    size,
  };

  const maxHeight = size === 'xsmall' ? XSMALL_DROPDOWN_COMPOSITE_MAX_HEIGHT : DEFAULT_DROPDOWN_COMPOSITE_MAX_HEIGHT;
  const defaultOptionWidth = getDropdownCompositeOptionWidth(size);
  const expanded = Boolean(valueProp);

  return (
    <>
      {isMounted && (
        <FloatingPortal id={FLOATING_ROOT_ID}>
          <FloatingFocusManager context={context} initialFocus={initialFocus} returnFocus={false}>
            <div
              {...getFloatingProps({
                ref: refs.setFloating,
                className: classNames('pds-DropdownComposite', className),
                style: {
                  ...floatingStyles,
                  zIndex,
                  outline: 'none',
                  ...styles,
                },
                onKeyDown(event: KeyboardEvent) {
                  if (event.key === 'ArrowUp' && (!focusIndex || focusIndex < 0)) {
                    setFocusIndex?.(options.length - 1);
                  }
                },
              })}
              css={[
                dropdown_item_group_css,
                { padding: 0 },
                { ...((unstableFocusVisible ?? isFocusVisible) && dropdown_item_group_focus_style) },
              ]}
              {...focusProps}
            >
              <SC.Container maxHeight={maxHeight}>
                <SC.Scrollable maxHeight={maxHeight} size={size} expanded={expanded}>
                  {options.length > 0 ? (
                    options.map((option, index) => {
                      return (
                        <DropdownCompositeInternalOption
                          key={index}
                          option={option}
                          index={index}
                          {...internalOptionProps}
                        />
                      );
                    })
                  ) : noDataText ? (
                    <SC.NoDataItem>{noDataText}</SC.NoDataItem>
                  ) : (
                    <SC.NoDataItem width={optionWidth ?? defaultOptionWidth}></SC.NoDataItem>
                  )}
                </SC.Scrollable>
                {expanded && (
                  <SC.DropdownCompositeInput
                    onKeyDown={(e) => {
                      if (e.key !== 'Escape') {
                        e.stopPropagation();
                      }
                    }}
                  >
                    <form
                      onSubmit={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        onClose();
                        onSubmit?.();
                      }}
                      onReset={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        onReset?.();
                      }}
                      style={{ minHeight: '100%', display: 'flex', flexDirection: 'column' }}
                    >
                      <Stack
                        direction='column'
                        flex={1}
                        gap={spacing.spacing_4}
                        p={size === 'xsmall' ? spacing.spacing_3 : spacing.spacing_4}
                        justify='space-between'
                      >
                        <FormField label={selectedOption.label} position='top'>
                          {selectedOption.children}
                        </FormField>
                        <Stack justify={selectedOption.footerExtra ? 'space-between' : 'flex-end'}>
                          {selectedOption.footerExtra}
                          <Stack gap={spacing.spacing_2} align='center'>
                            <TextButton
                              leftIcon={<IconReset />}
                              variant='text'
                              size='medium'
                              type='reset'
                              {...selectedOption.resetButtonProps}
                            >
                              초기화
                            </TextButton>
                            <Button
                              variant='filled'
                              color='primary'
                              type='submit'
                              {...selectedOption.submitButtonProps}
                            >
                              확인
                            </Button>
                          </Stack>
                        </Stack>
                      </Stack>
                    </form>
                  </SC.DropdownCompositeInput>
                )}
              </SC.Container>
            </div>
          </FloatingFocusManager>
        </FloatingPortal>
      )}
    </>
  );
};

const SC = {
  Container: styled('div', { shouldForwardProp: (prop) => prop !== 'maxHeight' })<{ maxHeight: number }>`
    display: flex;
    width: 100%;
    max-height: ${({ maxHeight }) => maxHeight}px;
    overflow: hidden;
  `,
  Scrollable: styled('ul', {
    shouldForwardProp: (prop) => !['maxHeight', 'size', 'expanded'].includes(prop),
  })<{ maxHeight: number; size: DropdownCompositeSize; expanded: boolean }>`
    display: flex;
    flex-direction: column;
    gap: ${({ size }) => (size === 'xsmall' ? 1 : 2)}px;
    min-width: ${DROPDOWN_COMPOSITE_LIST_MIN_WIDTH}px;
    margin: 0;
    padding: ${({ size }) => getInputSpacingBySize(size)}px;
    max-height: ${({ maxHeight }) => maxHeight}px;
    overflow: auto;
    ${({ expanded }) =>
      expanded &&
      `
        border-right: 1px solid ${semantic_colors.border.primary};
      `}
  `,
  DropdownCompositeInput: styled.div`
    box-sizing: border-box;
    min-width: 360px;
    max-height: 100%;
    overflow: auto;
  `,
  NoDataItem: styled(Stack)`
    color: ${semantic_colors.content.tertiary};
    cursor: default;
    &:hover,
    &:focus,
    &:active {
      background-color: transparent;
    }
  `,
};
